import { ReactElement } from 'react';

import { DayOfWeek, TimeOfDay } from '@dreryk/egabinet-waitlist-frontend-api';

import { BindingCreator, commonTranslationKeys, useTranslate } from '@dreryk/react-common';
import { FilterPanel, Multiselect, Select, StyledFlex } from '@dreryk/react-components';

import {
   WaitlistFilterPanelFormBean,
   WaitlistFilterPanelSearchParam,
   WaitlistFilterPanelStorageKey,
} from '../../WaitlistFilterPanel.types';
import { translationKeys } from '../../../translations';
import { DAYS_OF_WEEK } from '../../../form/fields/AllDaysPreferencesField/AllDaysPreferencesField.constants';
import { TIMES_OF_DAY } from '../../../form/fields/DayPreferenceField/DayPreferenceField.constants';

import {
   createPatientAvailabilityDayOfWeekTriggerLabelExtractor,
   createPatientAvailabilityTimesOfDayTriggerLabelExtractor,
   dayOfWeekControllerCodec,
   getDayOfWeekDeselectorsProps,
   timesOfDayControllerCodec,
   useTimesOfDayDeselectorsPropsMapper,
} from './PatientAvailabilityController.utils';
import { PatientAvailabilitySearchParam } from './PatientAvailability.types';

type PatientAvailabilityControllerProps = {
   registerController: BindingCreator<
      WaitlistFilterPanelFormBean,
      'patientAvailabilityDayOfWeek' | 'patientAvailabilityTimesOfDay'
   >;
};

// eslint-disable-next-line max-lines-per-function
export const PatientAvailabilityController = (props: PatientAvailabilityControllerProps): ReactElement => {
   const translate = useTranslate();
   const timesOfDayDeselectorsPropsMapper = useTimesOfDayDeselectorsPropsMapper();

   return (
      <StyledFlex gap={'8px'} alignItems={'flex-end'}>
         <FilterPanel.Controller<
            WaitlistFilterPanelFormBean,
            'patientAvailabilityDayOfWeek',
            PatientAvailabilitySearchParam
         >
            name={'patientAvailabilityDayOfWeek'}
            searchParam={WaitlistFilterPanelSearchParam.PATIENT_AVAILABILITY_DAY_OF_WEEK}
            storageKey={WaitlistFilterPanelStorageKey.PATIENT_AVAILABILITY_DAY_OF_WEEK}
            codec={dayOfWeekControllerCodec}
            chipsLabel={translationKeys.preferredDate}
            getDeselectorsProps={getDayOfWeekDeselectorsProps}
         >
            {({ applyFilter }) => (
               <Select<DayOfWeek>
                  testId={'dayOfWeek'}
                  triggerLabelExtractor={createPatientAvailabilityDayOfWeekTriggerLabelExtractor(translate)}
                  controller={props.registerController('patientAvailabilityDayOfWeek')}
                  onHide={applyFilter}
                  label={translationKeys.preferredDate}
                  placeholder={translationKeys.day}
                  width={'146px'}
                  compact
               >
                  <>
                     <Select.List<DayOfWeek> items={DAYS_OF_WEEK}>
                        {(item) => (
                           <Select.Item item={item} label={commonTranslationKeys.dayOfWeekMap[item]} testId={item} />
                        )}
                     </Select.List>
                  </>
               </Select>
            )}
         </FilterPanel.Controller>
         <FilterPanel.Controller<
            WaitlistFilterPanelFormBean,
            'patientAvailabilityTimesOfDay',
            PatientAvailabilitySearchParam
         >
            name={'patientAvailabilityTimesOfDay'}
            searchParam={WaitlistFilterPanelSearchParam.PATIENT_AVAILABILITY_TIMES_OF_DAY}
            storageKey={WaitlistFilterPanelStorageKey.PATIENT_AVAILABILITY_TIMES_OF_DAY}
            codec={timesOfDayControllerCodec}
            chipsLabel={translationKeys.preferredDate}
            getDeselectorsProps={timesOfDayDeselectorsPropsMapper}
         >
            {({ applyFilter }) => (
               <Multiselect<TimeOfDay>
                  testId={'timesOfDay'}
                  triggerLabelExtractor={createPatientAvailabilityTimesOfDayTriggerLabelExtractor(translate)}
                  controller={props.registerController('patientAvailabilityTimesOfDay')}
                  onHide={applyFilter}
                  placeholder={translationKeys.hours}
                  width={'206px'}
                  compact
               >
                  <>
                     <Multiselect.SelectAll
                        selectAllLabel={translationKeys.chooseAll}
                        testId={'chooseAllTimesOfDay'}
                        items={TIMES_OF_DAY}
                     />
                     <Multiselect.List<TimeOfDay> items={TIMES_OF_DAY}>
                        {(item) => (
                           <Multiselect.Item item={item} label={translationKeys.timeOfDayMap[item]} testId={item} />
                        )}
                     </Multiselect.List>
                  </>
               </Multiselect>
            )}
         </FilterPanel.Controller>
      </StyledFlex>
   );
};
